import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import { Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import PrivacyPolicyMain from "./PrivacyPolicyMain";
import TermsOfService from "./TermsOfService";
import DeletionStatusMain from "./data-deletion/DeletionStatusMain";
import { SUPPORTED_LANGS, normalizeLangParam } from "../locales";
import { setInterfaceLanguage } from "../../services/LangPrefs";
import { trackEvent } from "../../services/GoogleAnalytics";
import { shouldUseLegalAppShell, syncAppLegalWebViewState } from "../../config/appWebViewLegal";
import {
    APP_SITE_ID,
    isPrivacyPolicyPath,
    isTermsOfServicePath,
    resolveAppSiteEntry,
} from "../../config/appSiteLinks";

export default function LegalPage() {
    const location = useLocation();
    const { appId } = useParams();
    const [searchParams] = useSearchParams();
    const showDeletionStatus = location.hash === "#deletion-status";
    const invalidAppId = appId && !resolveAppSiteEntry(appId);

    const showTerms =
        !showDeletionStatus &&
        (location.hash === "#terms" || isTermsOfServicePath(location.pathname));
    const showPrivacy = !showDeletionStatus && !showTerms;

    useEffect(() => {
        syncAppLegalWebViewState(location.pathname, searchParams, location.hash);
    }, [location.pathname, searchParams, location.hash]);

    useEffect(() => {
        trackEvent("entered", { name: "LegalPage", path: location.pathname });
    }, [location.pathname]);

    // Apply language from ?language=french or ?lang=fr-FR query param
    useEffect(() => {
        const langParam =
            normalizeLangParam(searchParams.get("lang"))
            ?? normalizeLangParam(searchParams.get("language"));
        if (langParam && SUPPORTED_LANGS.includes(langParam)) {
            setInterfaceLanguage(langParam);
        }
    }, [searchParams]);

    // Keep hash in sync when using dedicated routes
    useEffect(() => {
        if (showDeletionStatus) return;
        if (isTermsOfServicePath(location.pathname)) {
            window.location.hash = "#terms";
        } else if (isPrivacyPolicyPath(location.pathname)) {
            window.location.hash = "#privacy";
        }
    }, [location.pathname, showDeletionStatus]);

    const legalAppShell = shouldUseLegalAppShell(
        location.pathname,
        location.search,
        location.hash,
    );

    if (invalidAppId) {
        const hash = location.hash || "";
        if (isTermsOfServicePath(location.pathname)) {
            return <Navigate to={`/terms_of_service/${APP_SITE_ID.WEB}${hash}`} replace />;
        }
        if (isPrivacyPolicyPath(location.pathname)) {
            return <Navigate to={`/privacy_policy/${APP_SITE_ID.WEB}${hash}`} replace />;
        }
        return <Navigate to={`/legal/${APP_SITE_ID.WEB}${hash}`} replace />;
    }

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                ...(legalAppShell ? { pt: { xs: 1, sm: 1.5 } } : {}),
            }}
        >
            <Box sx={{ mt: 0 }}>
                {showDeletionStatus ? (
                    <DeletionStatusMain />
                ) : (
                    <>
                        {showPrivacy && <PrivacyPolicyMain />}
                        {showTerms && <TermsOfService />}
                    </>
                )}
            </Box>
        </Box>
    );
}

